import { Box, Drawer, PaperProps } from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';
import { CollapsibleSideMenuZIndex } from '@/constant/z-index';

const DEFAULT_MENU_WIDTH = '33.333333%';
export type CollapsibleLayoutProps = PropsWithChildren<{
  readonly menu: ReactNode;
  readonly drawerStyle?: PaperProps['sx'];
  readonly topOffset?: number;
  readonly menuTopOffset: number;
  readonly layoutType: 'modal' | 'page';
  /** 열린 메뉴(드로어)의 너비. 퍼센트/px 모두 허용. 기본 33.333333% */
  readonly menuWidth?: string;

  readonly isDrawerOpen?: boolean;
  readonly keepMounted?: boolean;
}>;

const CollapsibleLayout = ({
  menu,
  layoutType,
  topOffset = 0,
  isDrawerOpen,
  menuTopOffset,
  drawerStyle = {},
  children,
  keepMounted = false,
  menuWidth = DEFAULT_MENU_WIDTH,
}: CollapsibleLayoutProps) => {
  const isLayoutPage = layoutType === 'page';
  const paperStyle = {
    '& .MuiDrawer-paper': {
      top: `${menuTopOffset}px`,
      height: isLayoutPage
        ? `calc(100vh - ${menuTopOffset}px)`
        : `calc(100% - ${menuTopOffset}px)`,
      width: menuWidth,

      transform: isDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 300ms ease',
      willChange: 'transform',

      overflowX: 'hidden',
      overflowY: 'auto',
      pointerEvents: isDrawerOpen ? 'auto' : 'none',
      ...drawerStyle,
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        paddingTop: topOffset ? `${topOffset}px` : 0,
        height: '100%',
      }}
    >
      <Box
        component="main"
        sx={{
          flex: `0 0 ${isDrawerOpen ? `calc(100% - ${menuWidth})` : '100%'}`,
          minWidth: 0,
          transition: 'flex-basis 0.3s ease',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Box>

      {/* Drawer */}
      <Drawer
        variant="persistent"
        open={isDrawerOpen}
        anchor="right"
        sx={{
          flex: `0 0 ${isDrawerOpen ? menuWidth : '0px'}`,
          flexShrink: 0,
          boxSizing: 'border-box',
          zIndex: CollapsibleSideMenuZIndex,
          transition: 'flex-basis 300ms ease',
          // root(도킹 스페이서)는 flex item 이라 static 이어도 위 zIndex 가 적용되어,
          // 열린 상태에서 우측 열 전체가 투명한 클릭 차단막이 된다(스크롤 시 모달
          // 확장/닫기 버튼까지 덮음). 실제 패널(paper)은 아래에서 pointerEvents 를
          // 따로 제어하므로 스페이서만 클릭을 통과시킨다.
          pointerEvents: 'none',
          ...paperStyle,
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {isDrawerOpen || keepMounted ? menu : null}
      </Drawer>
    </Box>
  );
};

export default CollapsibleLayout;
